import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Minus, Plus, ArrowLeft, Sparkle, Lock } from "@phosphor-icons/react";
import { useAuth, API } from "@/contexts/AuthContext";
import { PlantusLogo } from "@/components/plantus/Logo";

const EUR = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n);

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [pricing, setPricing] = useState(null);
  const [extraSeats, setExtraSeats] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Plantus Pro · Preise";
    axios.get(`${API}/pricing`).then((r) => setPricing(r.data)).catch(() => {});
    if (params.get("cancelled")) {
      toast.info("Checkout abgebrochen. Du kannst es jederzeit erneut starten.");
    }
  }, [params]);

  const totalYear = useMemo(() => {
    if (!pricing) return 0;
    return pricing.pro.price_year + extraSeats * pricing.pro.extra_seat_price_year;
  }, [pricing, extraSeats]);

  const seatsTotal = useMemo(() => {
    if (!pricing) return 3;
    return pricing.pro.seats_included + extraSeats;
  }, [pricing, extraSeats]);

  const sub = user?.subscription;
  const isProActive = sub?.tier === "pro";
  const isOwner = user?.role === "owner";

  const startCheckout = async () => {
    if (user === undefined) {
      // Auth-State laedt noch. bitte warten
      return;
    }
    if (user === null) {
      navigate(`/register?next=${encodeURIComponent("/preise")}`);
      return;
    }
    if (!isOwner) {
      toast.error("Nur der Betriebsinhaber kann das Abo abschließen.");
      return;
    }
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/app/stripe/create-checkout-session`, {
        intent: "subscribe",
        extra_seats: extraSeats,
        origin_url: origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Keine Checkout URL erhalten");
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Checkout konnte nicht gestartet werden.");
      setBusy(false);
    }
  };

  const openPortal = async () => {
    // Kein klassisches Stripe Customer Portal in diesem Modell:
    // Sub-Verwaltung passiert in /app/abo (Verlängern / Sitze hinzubuchen)
    navigate("/app/abo");
  };

  if (!pricing) {
    return <div className="min-h-screen flex items-center justify-center text-forest-700">Lade Preise…</div>;
  }

  return (
    <div className="min-h-screen bg-bone" data-testid="pricing-page">
      <header className="border-b border-bone-200/70 bg-bone/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="pricing-home">
            <PlantusLogo size="lg" />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/"
              data-testid="pricing-back-home"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-forest-700 hover:text-forest-900 px-3 py-2 rounded-full"
            >
              <ArrowLeft size={16} weight="bold" /> Zurück zur Startseite
            </Link>
            {user ? (
              <Link
                to="/app"
                className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white font-medium text-sm px-5 py-2.5 rounded-full transition-all shadow-sm hover:shadow-md"
              >
                Web-App
              </Link>
            ) : (
              <Link
                to="/login"
                className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white font-medium text-sm px-5 py-2.5 rounded-full transition-all shadow-sm hover:shadow-md"
              >
                Anmelden
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-forest-50 text-forest-700 text-xs font-medium mb-4 uppercase tracking-[0.18em]">
            <Sparkle size={14} weight="fill" /> Preise
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-forest-900 mb-4 tracking-tight">
            Klare Preise. Keine Pro&shy;visionen.
          </h1>
          <p className="font-body text-base text-[#4A524C]">
            Buche Plantus Pro direkt über unsere Website und spare die App-Store-Gebühren.
            Test&shy;keys aktiv, sichere Bezahlung via Stripe.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {/* Free Card */}
          <div
            className="bg-white rounded-3xl border border-bone-200 p-8 flex flex-col"
            data-testid="plan-free"
          >
            <div className="flex items-baseline justify-between">
              <h2 className="font-heading text-2xl font-semibold text-forest-900">Plantus Free</h2>
              <span className="text-xs uppercase tracking-[0.18em] text-[#7A827C]">Zum Ausprobieren</span>
            </div>
            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-heading text-5xl font-semibold text-forest-900">0 €</span>
              <span className="text-sm text-[#4A524C]">/ dauerhaft</span>
            </div>
            <p className="mt-4 text-sm text-[#4A524C]">
              Ideal um Plantus kennenzulernen und die Schnellerfassung zu testen.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-forest-900/90 flex-1">
              <Item>
                <strong>3 Anwendungen</strong> insgesamt (Test&shy;phase)
              </Item>
              <Item>Bis zu 3 Mitarbeiter</Item>
              <Item>Flächen anlegen, BVL-Suche</Item>
              <Item>PDF-Export deiner ersten Anwendungen</Item>
              <Item muted>Kein automatischer Abgleich der Behörden&shy;datenbank</Item>
            </ul>
            <div className="mt-8">
              {!user ? (
                <Link
                  to="/register"
                  data-testid="plan-free-cta"
                  className="block w-full text-center px-5 py-3 rounded-full border border-forest-600/30 text-forest-700 font-medium text-sm hover:bg-forest-50"
                >
                  Kostenlos starten
                </Link>
              ) : (
                <div className="text-center text-sm text-[#7A827C]">Aktiver Tarif: <strong>{sub?.tier === "free" ? "Free" : "Pro"}</strong></div>
              )}
            </div>
          </div>

          {/* Pro Card */}
          <div
            className="relative bg-forest-900 text-white rounded-3xl p-8 flex flex-col shadow-xl ring-1 ring-forest-900/10"
            data-testid="plan-pro"
          >
            <div className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-bone text-forest-900 text-[11px] font-semibold uppercase tracking-[0.18em] px-3 py-1 rounded-full">
              <Sparkle size={12} weight="fill" /> Empfohlen
            </div>
            <div className="flex items-baseline justify-between">
              <h2 className="font-heading text-2xl font-semibold">Plantus Pro</h2>
              <span className="text-xs uppercase tracking-[0.18em] text-bone/70">Jahresabo</span>
            </div>
            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-heading text-5xl font-semibold" data-testid="pricing-total">{EUR(totalYear)}</span>
              <span className="text-sm opacity-80">/ Jahr</span>
            </div>
            <p className="mt-2 text-xs opacity-70">
              {EUR(pricing.pro.price_year)} Basis · {seatsTotal} Mitarbeiter
            </p>

            <ul className="mt-8 space-y-3 text-sm flex-1">
              <Item dark>
                <strong>Unbegrenzte Anwendungen</strong> dokumentieren
              </Item>
              <Item dark>
                <strong>3 Mitarbeiter</strong> inklusive (jeder weitere {EUR(pricing.pro.extra_seat_price_year)} / Jahr)
              </Item>
              <Item dark>Automatischer BVL Sync (Behörden&shy;datenbank)</Item>
              <Item dark>PDF-Export &amp; JSON-Export (EU 2023/564)</Item>
              <Item dark>GPS, Wetter, Wartezeit automatisch</Item>
              <Item dark>Prioritäts-Support</Item>
            </ul>

            <div className="mt-7 rounded-2xl bg-white/10 p-4 backdrop-blur" data-testid="seat-picker">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] opacity-70">Zusätzliche Mitarbeiter</div>
                  <div className="font-heading text-lg font-semibold">
                    {extraSeats} extra · gesamt {seatsTotal}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setExtraSeats((s) => Math.max(0, s - 1))}
                    data-testid="seat-minus"
                    className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
                    aria-label="Weniger"
                  >
                    <Minus size={16} weight="bold" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setExtraSeats((s) => Math.min(200, s + 1))}
                    data-testid="seat-plus"
                    className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
                    aria-label="Mehr"
                  >
                    <Plus size={16} weight="bold" />
                  </button>
                </div>
              </div>
              <div className="mt-2 text-xs opacity-70">
                Du kannst zusätzliche Sitze auch später im Abo-Bereich hinzubuchen.
              </div>
            </div>

            <div className="mt-7">
              {isProActive ? (
                <div className="space-y-2">
                  <div className="bg-forest-50 text-forest-900 rounded-2xl px-4 py-3 text-sm flex items-center gap-2">
                    <Check size={16} weight="bold" /> Aktives Pro-Abo
                  </div>
                  {isOwner && (
                    <button
                      type="button"
                      onClick={openPortal}
                      disabled={busy}
                      data-testid="plan-pro-portal"
                      className="block w-full text-center px-5 py-3 rounded-full bg-white text-forest-900 hover:bg-bone font-medium text-sm"
                    >
                      Abo verwalten
                    </button>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={startCheckout}
                  disabled={busy}
                  data-testid="plan-pro-cta"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white text-forest-900 hover:bg-bone font-semibold text-sm transition-all shadow-sm hover:shadow-md disabled:opacity-60"
                >
                  {busy ? "Weiterleitung…" : `Pro abschließen · ${EUR(totalYear)} / Jahr`}
                </button>
              )}
              <div className="mt-3 text-[11px] opacity-70 flex items-center justify-center gap-1.5">
                <Lock size={12} weight="bold" /> Sicher bezahlen mit Stripe · Verlängert sich automatisch · 1 Monat Kündigungsfrist
              </div>
              <PaymentMethods methods={pricing.payment_methods} />
            </div>
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-6 max-w-5xl mx-auto text-sm text-[#4A524C]">
          <FaqMini title="Kann ich jederzeit kündigen?">
            Ja. Das Abo verlängert sich automatisch um ein weiteres Jahr. du kannst jederzeit mit einer Frist von 1 Monat zum Ende der Laufzeit im Abo-Bereich kündigen. Wir erinnern dich 30 Tage vor der Verlängerung per Mail.
          </FaqMini>
          <FaqMini title="Was passiert mit meinen Daten?">
            Alle deine Aufzeichnungen bleiben erhalten. Im Free Tarif kannst du Bestandsdaten weiterhin exportieren.
          </FaqMini>
          <FaqMini title="Brauche ich die Abrechnung im App Store?">
            Nein. Du buchst hier direkt über Stripe und sparst die Apple/Google Provision.
          </FaqMini>
        </div>
      </main>
    </div>
  );
}

const Item = ({ children, dark, muted }) => (
  <li className={`flex items-start gap-2 ${muted ? "opacity-60" : ""}`}>
    <span className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full ${dark ? "bg-white/15" : "bg-forest-50"}`}>
      <Check size={12} weight="bold" className={dark ? "text-white" : "text-forest-700"} />
    </span>
    <span>{children}</span>
  </li>
);

const FaqMini = ({ title, children }) => (
  <div className="bg-white/60 rounded-2xl border border-bone-200 p-5">
    <div className="font-heading font-semibold text-forest-900 mb-1.5">{title}</div>
    <div>{children}</div>
  </div>
);

const PAYMENT_LABELS = {
  card: "Kreditkarte",
  sepa_debit: "SEPA-Lastschrift",
  paypal: "PayPal",
  klarna: "Klarna",
  giropay: "Giropay",
  sofort: "Sofortüberweisung",
  bancontact: "Bancontact",
  eps: "EPS",
  ideal: "iDEAL",
  p24: "Przelewy24",
  link: "Link",
};

const PaymentMethods = ({ methods }) => {
  if (!Array.isArray(methods) || methods.length === 0) return null;
  return (
    <div className="mt-4 pt-4 border-t border-white/15" data-testid="payment-methods">
      <div className="text-[10px] uppercase tracking-[0.18em] opacity-60 text-center mb-2">
        Akzeptierte Zahlungsmethoden
      </div>
      <div className="flex items-center justify-center gap-1.5 flex-wrap">
        {methods.map((m) => (
          <span
            key={m}
            data-testid={`pm-${m}`}
            className="inline-flex items-center gap-1 text-[11px] font-medium bg-white/12 px-2.5 py-1 rounded-full"
          >
            <PaymentIcon method={m} />
            {PAYMENT_LABELS[m] || m}
          </span>
        ))}
      </div>
    </div>
  );
};

const PaymentIcon = ({ method }) => {
  if (method === "card")
    return (
      <svg width="16" height="11" viewBox="0 0 24 16" fill="none" aria-hidden="true">
        <rect width="24" height="16" rx="2" fill="currentColor" opacity="0.9" />
        <rect y="4" width="24" height="2.5" fill="#0b1f15" />
        <rect x="3" y="10" width="6" height="2" rx="0.5" fill="#0b1f15" />
      </svg>
    );
  if (method === "sepa_debit")
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 7h16M6 11h12M8 15h8M10 19h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  if (method === "paypal")
    return (
      <svg width="12" height="14" viewBox="0 0 24 28" fill="none" aria-hidden="true">
        <path d="M7 4h8a5 5 0 0 1 5 5c0 4-3 7-7 7h-3l-1.5 8H4L7 4z" fill="currentColor" />
      </svg>
    );
  return <span className="w-2 h-2 rounded-full bg-current" />;
};
